import { Box, Button, Checkbox, Container, Grid, Typography, useTheme } from "@mui/material"
import { ClickAwayListener, Unstable_Popup as Popup } from "@mui/base"
import { FieldContainer, PageTitle, StyledTextField } from "../components/StyledElements"
import React, { useEffect, useState } from "react"
import { usePlaylistAPI } from "../services/api/hooks/usePlaylistAPI"
import { PlaylistQueryType, PlaylistType } from "../services/api/playlist"
import { useNavigate, useParams } from "react-router-dom"
import { useSnackbarContext } from "../services/snackbar"
import { httpClient } from "../services/http"
import { useStaticContext } from "../services/static"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { VideoQueryType, VideoType } from "../services/api/video"
import { VideoThumbnailImage } from "../components/VideoThumbnail"

import { ReactComponent as CrossIcon } from "../assets/svg/cross.svg"

export const EditPlaylist = (): React.ReactElement => {
  const { playlistCode } = useParams<{ playlistCode: string }>()
  const { open: sbOpen } = useSnackbarContext()
  const { reload: reloadStatic } = useStaticContext()
  const { role } = useAuthContext()
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    playlistsData: playlistData,
    loading: playlistLoading,
    error: playlistError,
  } = usePlaylistAPI({
    constraint: PlaylistQueryType.PLAYLIST,
    code: playlistCode,
  })
  const {
    videosData,
    loading: videosLoading,
    error: videosError,
    refresh: refreshVideos,
  } = useVideoAPI({
    constraint: VideoQueryType.PLAYLIST,
    code: playlistCode,
  })

  const [originalPlaylist, setOriginalPlaylist] = useState<PlaylistType | null>(null)
  const [title, setTitle] = useState<string>("")
  const [description, setDescription] = useState<string>("")
  const [isPublic, setPublic] = useState<boolean>(false)
  const [videos, setVideos] = useState<VideoType[]>([])
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false)

  useEffect(() => {
    if (playlistLoading) return
    if (playlistError) {
      sbOpen("Erreur pendant le chargement de la playlist", "error")
      navigate("/playlists")
      return
    }
    const playlist = playlistData[0]

    setOriginalPlaylist(playlist)
    setTitle(playlist.title)
    setDescription(playlist.description)
    setPublic(playlist.isPublic)
  }, [playlistLoading, playlistError])

  useEffect(() => {
    if (videosLoading) return
    if (videosError) {
      sbOpen("Erreur pendant le chargement des vidéos de la playlist", "error")
      navigate("/playlists")
      return
    }
    setVideos(videosData)
  }, [videosLoading, videosError])

  const handleSubmit = async () => {
    const data = {
      title: title,
      description: description,
      isPublic: isPublic,
    }

    try {
      await httpClient.instance.put(`playlists/${playlistCode}`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
    } catch (error) {
      sbOpen("Erreur pendant la modification de la playlist", "error")
    } finally {
      sbOpen("Playlist modifiée avec succès", "success")
      reloadStatic()
      navigate(`/playlist?pl=${playlistCode}`)
    }
  }

  const handleDelete = async () => {
    try {
      await httpClient.instance.delete(`playlists/${playlistCode}`)
      sbOpen("Playlist supprimée avec succès", "success")
    } catch (error) {
      sbOpen("Erreur pendant la suppression de la playlist", "error")
    } finally {
      reloadStatic()
      navigate("/playlists")
    }
  }

  const handleRemoveVideo = async (videoId: number) => {
    try {
      await httpClient.instance.delete(`playlists/${playlistCode}/removeVideo/${videoId}`)
    } catch (error) {
      sbOpen("Erreur pendant la suppression de la vidéo", "error")
    } finally {
      refreshVideos()
    }
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("text/plain", String(index))
    setDragIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop = async (e: React.DragEvent, index: number) => {
    e.preventDefault()
    const fromStr = e.dataTransfer.getData("text/plain")
    if (!fromStr) return
    const from = parseInt(fromStr, 10)
    const to = index
    if (isNaN(from)) return
    if (from === to) {
      setDragIndex(null)
      return
    }

    const newVideos = [...videos]
    const [moved] = newVideos.splice(from, 1)
    newVideos.splice(to, 0, moved)
    setVideos(newVideos)

    try {
      const video_ids = newVideos.map((v) => v.id)
      await httpClient.instance.put(`playlists/${playlistCode}/reorderVideos`, video_ids, {
        headers: { "Content-Type": "application/json" },
      })
    } catch (err) {
      sbOpen("Erreur lors du changement d'ordre", "error")
    } finally {
      refreshVideos()
      setDragIndex(null)
    }
  }

  return originalPlaylist ? (
    <>
      <PageTitle>{originalPlaylist?.title}</PageTitle>
      <Container>
        <Grid container direction="row">
          <Grid size={{ xs: 12, md: 6 }} container display="block">
            <FieldContainer size={12}>
              <StyledTextField
                label="Titre"
                variant="standard"
                color="soft"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                focused
              />
            </FieldContainer>
            <FieldContainer size={12}>
              <StyledTextField
                label="Description"
                variant="outlined"
                color="soft"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={8}
                maxRows={12}
                focused
              />
            </FieldContainer>
            {role === UserRole.GEEK && (
              <FieldContainer size={12}>
                <Checkbox checked={isPublic} color="soft" onChange={(e) => setPublic(e.target.checked)} />
                <Typography variant="body1" component="span">
                  {"Featuré"}
                </Typography>
              </FieldContainer>
            )}
            <FieldContainer size={12}>
              <Typography variant="body1" component="span">
                {"Pour modifier l'ordre des vidéos, faites-les glisser dans la liste (ne fonctionne pas sur mobile)."}
              </Typography>
            </FieldContainer>
            <FieldContainer size={12}>
              <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={title === ""}>
                {"Valider"}
              </Button>
              <Button
                variant="contained"
                color="error"
                sx={{ margin: 1 }}
                onClick={() => setDeletePopupOpen(true)}
                id="delete-button"
              >
                {"Supprimer"}
              </Button>
              <Popup
                anchor={document.querySelector("#delete-button")}
                open={isDeletePopupOpen}
                placement="top"
                offset={10}
              >
                <ClickAwayListener onClickAway={() => setDeletePopupOpen(false)}>
                  <Box
                    padding={2}
                    borderRadius="5px"
                    sx={{
                      backgroundColor: theme.palette.element.contextMenu?.background ?? theme.palette.primary.main,
                      backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
                    }}
                  >
                    <Typography>{"Etes-vous sûr de vouloir supprimer cette playlist ?"}</Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ margin: 1 }}
                      onClick={() => setDeletePopupOpen(false)}
                    >
                      {"Annuler"}
                    </Button>
                    <Button variant="outlined" color="error" onClick={handleDelete} sx={{ margin: 1 }}>
                      {"Oui, supprimer"}
                    </Button>
                  </Box>
                </ClickAwayListener>
              </Popup>
            </FieldContainer>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box margin={4}>
              {videos.map((video, index) => (
                <Box
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={() => setDragIndex(null)}
                  key={video.id}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: 1,
                    bgcolor: theme.palette.primary.dark,
                    maxHeight: "100px",
                    // slightly fade the dragged item
                    opacity: dragIndex === index ? 0.6 : 1,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                    },
                    transition: "background-color 0.2s",
                  }}
                >
                  <Box sx={{ display: { xs: "none", sm: "block" }, width: "40%", maxWidth: "150px", marginRight: 1 }}>
                    <VideoThumbnailImage video={video} adaptiveBorder={false} />
                  </Box>
                  <Box sx={{ width: "60%" }}>
                    <Typography
                      marginLeft={1}
                      variant="h5"
                      fontSize={"1.2rem"}
                      overflow={"hidden"}
                      textOverflow="ellipsis"
                    >
                      {video.title}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ml: 2,
                      flex: "0 0 auto",
                      display: "flex",
                      alignItems: "center",
                      padding: "5px",
                      borderRadius: "50%",
                      "&:hover": { backgroundColor: "grey" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <CrossIcon
                      width={"25px"}
                      height={"25px"}
                      fill={theme.palette.mode === "dark" ? "white" : "black"}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        handleRemoveVideo(video.id)
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  ) : (
    <Typography color="textPrimary">{"Chargement..."}</Typography>
  )
}
